import { TooltipContent } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

import { ClickToCopy } from "@/components/ClickToCopy";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { Case, InputDocumentMetadata } from "@/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { loadAndFormatDate } from "@/utils/dateFormat";
import { StatusBadge } from "@/components/Status";
import { DataTable } from "@/components/table/Table";
import { useEffect, useRef } from "react";
import { useAuthInfo } from "@propelauth/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { ReloadIcon } from "@radix-ui/react-icons";
import {
  deleteInputDocument,
  reProcessInputDocument,
  updateInputDocumentAssociation,
  uploadItems,
  downloadInputDocument,
} from "@/utils/apiCalls";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { SearchFilter } from "./SelectFilter";
import { cn } from "@/lib/utils";
import { useDrawer } from "@/providers/drawer/DrawerContext";
import PdfViewer from "../pdfViewer/PDFViewer";

export const FileUploadButton = (props: { caseId: string }) => {
  const authInfo = useAuthInfo();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    setUploading(true);
    const newDocs = await uploadItems(
      props.caseId,
      formData,
      authInfo.accessToken ?? null
    );
    if (newDocs !== null) {
      window.location.reload();
      toast.success("Files uploaded successfully");
    } else {
      toast.error("Failed to upload files");
    }
    setUploading(false);
  };

  return (
    <div>
      <Button onClick={handleButtonClick}>
        {uploading ? (
          <ReloadIcon className="w-4 h-4 animate-spin" />
        ) : (
          <UploadIcon className="w-4 h-4" />
        )}
        Upload Files
      </Button>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        multiple
      />
    </div>
  );
};

type InputDocumentTableProps = {
  data: InputDocumentMetadata[];
  caseId: string;
  setCase: React.Dispatch<React.SetStateAction<Case | null>>;
};

export const InputDocumentTable: React.FC<InputDocumentTableProps> = ({
  data,
  caseId,
  setCase,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const authInfo = useAuthInfo();
  const { openDrawer } = useDrawer();

  const tableData = data.filter(
    (document) =>
      document.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      searchTerm === ""
  );

  const handleReProcess = async (documentId: string) => {
    const response = await reProcessInputDocument(
      documentId,
      authInfo.accessToken ?? null
    );
    if (response) {
      toast.success("Document re-processing started");
      setCase((prev) => {
        if (prev === null) return null;
        return {
          ...prev,
          input_documents: prev.input_documents.map((doc) => {
            if (doc.id === documentId) {
              return {
                ...doc,
                status: "processing",
              };
            }
            return doc;
          }),
        };
      });
    } else {
      toast.error("Failed to re-process document");
    }
  };

  const handleDelete = async (documentId: string) => {
    const response = await deleteInputDocument(
      documentId,
      authInfo.accessToken ?? null
    );
    if (response) {
      toast.success("Document deleted successfully");
      setCase((prev) => {
        if (prev === null) return null;
        return {
          ...prev,
          input_documents: prev.input_documents.filter(
            (doc) => doc.id !== documentId
          ),
        };
      });
    } else {
      toast.error("Failed to delete document");
    }
  };

  const handleRowClick = async (
    row: Row<InputDocumentMetadata>,
    event: React.MouseEvent
  ) => {
    // Check if the click target is a checkbox or its parent
    const target = event.target as HTMLElement;
    const checkbox = target.closest('input[type="checkbox"]');
    if (checkbox) {
      event.stopPropagation();
      return;
    }

    if (row.original.mime_type === "application/pdf") {
      openDrawer({
        documentId: row.original.id,
        title: row.original.name,
      });
    } else {
      toast.info("Can only preview PDF documents");
    }
  };

  const handleUpdateAssociation = async (
    documentId: string,
    recordReview: boolean | null,
    qmeSections: boolean | null,
    supplementalReport: boolean | null
  ) => {
    const response = await updateInputDocumentAssociation(
      documentId,
      recordReview,
      qmeSections,
      supplementalReport,
      authInfo.accessToken ?? null
    );

    if (response) {
      setCase((prev) => {
        if (prev === null) return null;
        return {
          ...prev,
          input_documents: prev.input_documents.map((doc) =>
            doc.id === documentId
              ? {
                  ...doc,
                  record_review:
                    recordReview !== null ? recordReview : doc.record_review,
                  qme_sections:
                    qmeSections !== null ? qmeSections : doc.qme_sections,
                  supplemental_report:
                    supplementalReport !== null
                      ? supplementalReport
                      : doc.supplemental_report,
                }
              : doc
          ),
        };
      });
      toast.success("Document association updated successfully");
    } else {
      toast.error("Failed to update document association");
    }
  };

  const handleDownload = async (documentId: string) => {
    const response = await downloadInputDocument(
      documentId,
      authInfo.accessToken ?? null,
      false
    );
    if (response !== null) {
      toast.success("Document downloaded successfully");
    } else {
      toast.error("Failed to download document");
    }
  };

  const columns: ColumnDef<InputDocumentMetadata>[] = [
    {
      accessorKey: "id",
      header: "ID",
      enableSorting: false,
      cell: ({ row }: any) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <ClickToCopy
                text={row.original.id}
                className="max-w-[50px] text-ellipsis overflow-hidden whitespace-nowrap"
              />
            </TooltipTrigger>
            <TooltipContent>{row.original.id}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "date",
      header: "Date",
      sortingFn: (a, b) =>
        new Date(a.original.created_at).getTime() -
        new Date(b.original.created_at).getTime(),
      cell: ({ row }: any) => {
        return <div>{loadAndFormatDate(row.original.created_at)}</div>;
      },
    },
    {
      accessorKey: "created_by",
      header: "Uploaded By",
      enableSorting: false,
      cell: ({ row }: any) => {
        return <div>{row.original.created_by}</div>;
      },
    },
    {
      accessorKey: "name",
      header: "Name",
      enableSorting: false,
      cell: ({ row }: any) => {
        return <div>{row.original.name}</div>;
      },
    },
    {
      accessorKey: "size_mb",
      header: "Size",
      cell: ({ row }: any) => {
        return <div>{row.original.size_mb.toFixed(1)} MB</div>;
      },
    },
    {
      id: "qme-sections",
      header: "QME Sections",
      cell: ({ row }) => {
        const status = row.original.status;
        const qmeSections = row.original.qme_sections;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={qmeSections === true}
              disabled={status !== "completed"}
              onCheckedChange={(checked) => {
                handleUpdateAssociation(
                  row.original.id,
                  null,
                  checked === true ? true : false,
                  null
                );
              }}
            />
          </div>
        );
      },
    },
    {
      id: "record-review",
      header: "Record Review",
      cell: ({ row }) => {
        const status = row.original.status;
        const recordReview = row.original.record_review;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={recordReview === true}
              disabled={status !== "completed"}
              onCheckedChange={(checked) => {
                handleUpdateAssociation(
                  row.original.id,
                  checked === true ? true : false,
                  null,
                  null
                );
              }}
            />
          </div>
        );
      },
    },
    {
      id: "supplemental-report",
      header: "Supplemental Report",
      cell: ({ row }) => {
        const status = row.original.status;
        const supplementalReport = row.original.supplemental_report;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={supplementalReport === true}
              disabled={status !== "completed"}
              onCheckedChange={(checked) => {
                handleUpdateAssociation(
                  row.original.id,
                  null,
                  null,
                  checked === true ? true : false
                );
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      enableSorting: false,
      cell: ({ row }: any) => {
        return <StatusBadge status={row.original.status} />;
      },
    },
    {
      id: "controls",
      cell: ({ row }) => {
        const documentId = row.original.id;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownload(documentId);
                }}
              >
                Download
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleReProcess(documentId);
                }}
              >
                Re-Process
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(documentId);
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-md text-muted-foreground">
          {tableData.length} documents
        </div>
        <div className="flex items-center space-x-2">
          <SearchFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder="Search Documents..."
            className={cn("w-[200px]")}
          />
          <FileUploadButton caseId={caseId} />
        </div>
      </div>
      <DataTable
        data={tableData}
        columns={columns}
        enableSorting
        onRowClick={handleRowClick}
      />
    </div>
  );
};
